// src/annihilation/skilltrees/universal.js
// ============================================================
//  ANNIHILATION — the Universal Tree.
//  Source of truth: ANNIHILATION_SKILL_TREES (v2), Part 3.
//
//  20 nodes · 7 clusters. Identical for all four launch beings —
//  ONE file, never four copies. Shadow, Echo, and Mirage live
//  here (Mirage requires BOTH Shadow and Echo unlocked — the
//  payoff for investing in both fundamentals).
//
//  ⚑⚑ THE CUT + THE VIGIL (Power Doctrine · Jul 25 2026) ⚑⚑
//
//  FIVE NODES CUT, all by one rule:
//
//    > THE DIAL-IS-NOT-A-NODE LAW. If a node's entire effect is
//    > "the number in the base ability is now a bigger number,"
//    > it is not a node. It is a balance pass wearing a node's
//    > costume. Cut it, and fold its value into the base.
//
//  A skill point here is SCARCE AND PERMANENT. Spending one to
//  turn 10 seconds into 15 seconds is not a decision — it is a
//  tax on a player who already decided.
//    · uni_m1  Quickened Dodge  (+25% roll distance)   → base
//    · uni_p1  Big Drink        (+30% potion)          → base
//    · uni_s2  Shadow Reach     (20m → 30m)            → base
//    · uni_ec2 Echo Timing      (2s → 1s delay)        → base
//    · uni_mr2 Longer Mirage    (10s → 15s)            → base
//  Every cut value FOLDS INTO its base ability — the player
//  loses five buttons and zero power.
//
//  FIVE NODES ADDED: the two second-slot unlocks for Senses and
//  Instincts (senses.js), and the three Vigil nodes — the first
//  time the Chao Growth Law has ever been buildable in an IE
//  title. 20 in, 20 out, and every survivor is a decision.
//
//  ⚑ A save holding a cut id gets its point RETURNED by
//  treeEngine.reconcileTreeState. Points are scarce and
//  permanent; a deleted node may never silently eat one.
//
//  Pure data; treeEngine is the only legality gate.
// ============================================================

import { NODE_TYPES, TREE_IDS, AENERGY_REGISTER } from "./treeEngine";

export const UNIVERSAL_CLUSTERS = {
  movement: { id: "movement", name: "Movement Cluster", focus: "Dodge and traversal" },
  potion: { id: "potion", name: "Potion Cluster", focus: "Consumable efficiency" },
  shadow: { id: "shadow", name: "Shadow Cluster", focus: "Ranged phantom attacks" },
  echo: { id: "echo", name: "Echo Cluster", focus: "Delayed repeats of your actions" },
  mirage: {
    id: "mirage",
    name: "Mirage Cluster",
    focus: "Shadow + Echo fusion into a clone",
  },
  vigil: {
    id: "vigil",
    name: "Vigil Cluster",
    focus: "Restraint compounds — the Chao Growth Law, built",
  },
  perception: {
    id: "perception",
    name: "Perception Cluster",
    focus: "Second Sense and second Instinct slots",
  },
};

const P = NODE_TYPES.PASSIVE;
const A = NODE_TYPES.ACTIVE;
const C = NODE_TYPES.CAPSTONE;

export const UNIVERSAL_TREE = {
  treeId: TREE_IDS.UNIVERSAL,
  name: "Universal",
  nodeSlots: 20, // 15 survivors + 5 new (Power Doctrine)
  clusters: UNIVERSAL_CLUSTERS,
  nodes: [
    // ---------------- MOVEMENT (4) ----------------
    {
      id: "uni_m2",
      name: "Dodge Mastery",
      type: P,
      cluster: "movement",
      effect: "Dodge iframes extended from 0.3 to 0.5 seconds.",
    },
    {
      id: "uni_m3",
      name: "Second Wind",
      type: P,
      cluster: "movement",
      effect: "Dodging restores 1 Lyt on successful evasion of a telegraphed attack.",
    },
    {
      id: "uni_m4",
      name: "Swift Traveler",
      type: P,
      cluster: "movement",
      effect: "Out-of-combat movement speed +20%. Exploration-focused.",
    },
    {
      id: "uni_m5",
      name: "Phantom Roll",
      type: C,
      cluster: "movement",
      effect:
        "Dodge is no longer bound by terrain — pass through enemies, walls, and hazards for the duration of the roll.",
    },

    // ----------------- POTION (2) -----------------
    {
      id: "uni_p2",
      name: "Quick Drink",
      type: P,
      cluster: "potion",
      effect: "Healing potion animation is 50% faster. Chuggable safely in combat.",
    },
    {
      id: "uni_p3",
      name: "Shared Potion",
      type: P,
      cluster: "potion",
      effect:
        "Healing potions also restore 25% of their Anima value to nearby party members. Party-support role.",
    },

    // ----------------- SHADOW (3) -----------------
    {
      id: "uni_s1",
      name: "Shadow",
      type: A,
      cluster: "shadow",
      effect:
        "Unlocks the Shadow universal ability — a ranged phantom attack dealing base Attack + 10 flat, out to 30 metres. Cooldown 8 sec.",
      // ⚑ FOLDED IN by THE CUT: uni_s2 Shadow Reach was a
      // 20m → 30m dial. The range is base now; the button is gone.
      rangeMeters: 30,
      cooldownSec: 8,
    },
    {
      id: "uni_s3",
      name: "Shadow Stacks",
      type: P,
      cluster: "shadow",
      requires: ["uni_s1"],
      effect: "Shadow can be stored — hold up to 2 charges. Cooldown applies per-charge.",
    },
    {
      id: "uni_s4",
      name: "Shadow Apex",
      type: C,
      cluster: "shadow",
      requires: ["uni_s1"],
      effect:
        "Shadow applies your lineage's Lyt-secondary effect on hit (Spectral Tiger: Bleed if owned; Chaos Dragon: +1 Chaos stack; Z4Z: small scarab spawn chance; Xulan: Vessel Mark).",
    },

    // ------------------ ECHO (3) ------------------
    {
      id: "uni_ec1",
      name: "Echo",
      type: A,
      cluster: "echo",
      effect:
        "Unlocks the Echo universal ability — your last-used Primera or Impera repeats once, 1 second after you cast it. Cooldown 15 sec.",
      // ⚑ FOLDED IN by THE CUT: uni_ec2 Echo Timing was a
      // 2s → 1s dial. The immediacy is base now.
      delaySec: 1,
      cooldownSec: 15,
    },
    {
      id: "uni_ec3",
      name: "Echo Resonance",
      type: P,
      cluster: "echo",
      requires: ["uni_ec1"],
      effect: "Echo's repeat deals +20% bonus damage compared to the original cast.",
    },
    {
      id: "uni_ec4",
      name: "Long Echo",
      type: C,
      cluster: "echo",
      requires: ["uni_ec1"],
      effect:
        "Echo can repeat your last U'hlti, not just Primera/Impera. Cooldown extends to 60 sec when used on a U'hlti.",
    },

    // ----------------- MIRAGE (3) -----------------
    {
      id: "uni_mr1",
      name: "Mirage",
      type: A,
      cluster: "mirage",
      requires: ["uni_s1", "uni_ec1"], // Shadow AND Echo — the fusion payoff
      effect:
        "Fuses Shadow + Echo into a 15-second clone of yourself that fights independently. Rest-gated: once per village visit.",
      // ⚑ FOLDED IN by THE CUT: uni_mr2 Longer Mirage was a
      // 10s → 15s dial. Every Mirage ships on a timer (Gating
      // Doctrine Part 8) — Aenergy-fed indefinite duration is an
      // exception granted case by case, never the default.
      durationSec: 15,
      restGated: true,
    },
    {
      id: "uni_mr3",
      name: "Mimic Clone",
      type: P,
      cluster: "mirage",
      requires: ["uni_mr1"],
      effect:
        "Mirage copies your CURRENT skill rotation — cast a Primera and the Mirage casts it on its own target. A full fighter, not a distraction.",
    },
    {
      id: "uni_mr4",
      name: "Twin Mirage",
      type: C,
      cluster: "mirage",
      requires: ["uni_mr1"],
      effect: "Mirage can proc twice per village visit instead of once. Endgame clone-army build.",
    },

    // ----------------- VIGIL (3) ------------------
    // ⚑ THE VIGIL (stats.js) is the Empire's second Pillar and
    // the single highest-value rule Terror has: restraint
    // compounds. An engagement closed without spending one point
    // of Chao permanently raises your Max Chao — but ONLY if
    // your Anima was dimmed, so restraint has to have been
    // dangerous to pay. These three nodes are the first time it
    // has ever been buildable in any IE title.
    {
      id: "uni_v1",
      name: "The Long Vigil",
      type: P,
      cluster: "vigil",
      aenergyReq: AENERGY_REGISTER.CHAMPION_SOFT,
      effect: "A completed Vigil grants +3 Max Chao instead of +2.",
      vigilGrantOverride: 3,
    },
    {
      id: "uni_v2",
      name: "Deep Vigil",
      type: P,
      cluster: "vigil",
      requires: ["uni_v1"],
      aenergyReq: AENERGY_REGISTER.CHAMPION_HARD,
      effect:
        "While holding an unbroken Vigil, your Decree vigil trigger is worth 4 instead of 2. Restraint hits harder, not just later.",
      decreeTriggerOverride: { vigil: 4 },
    },
    {
      id: "uni_v3",
      name: "The Unbroken Vigil",
      type: C,
      cluster: "vigil",
      requires: ["uni_v1"],
      aenergyReq: AENERGY_REGISTER.LORD_SOFT,
      effect:
        "A Vigil is no longer broken by Tome fire — only by Impera, U'hlti, Sense, Instinct, Shadow, Echo or Mirage. The Vigil-Tome build: win the whole fight by Setting answers and swinging.",
      vigilIgnoresSpendKinds: ["tome"],
    },

    // -------------- PERCEPTION (2) ----------------
    // The second-slot unlocks for senses.js. Priced Lord-hard
    // and NOT tier-gated — a Beb who hoards every point can hold
    // two Senses. It will have cost them nearly everything, and
    // THE STEERING LAW says that is allowed.
    {
      id: "uni_sn1",
      name: "The Second Sense",
      type: A,
      cluster: "perception",
      aenergyReq: AENERGY_REGISTER.LORD_HARD,
      effect: "A second Sense slot. Read the world twice at once.",
    },
    {
      id: "uni_in1",
      name: "The Second Instinct",
      type: A,
      cluster: "perception",
      aenergyReq: AENERGY_REGISTER.LORD_HARD,
      effect: "A second Instinct slot. The body gets a second opinion.",
    },
  ],
};

export default UNIVERSAL_TREE;
